import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTimes } from "@fortawesome/free-solid-svg-icons";
import useSubjectAPI from "./useSubjectAPI";
import { titles, genders } from "../enums";

const PersonalInformation = ({ subjectId, form }) => {
  const purple = "#2E2868";
  const pillBg = "#ebeaf3";

  const isCreate = !subjectId;
  const [open, setOpen] = useState(true);

  const { loadSubject, saveSubjectDirect } = useSubjectAPI(subjectId);

  const [values, setValues] = useState({
    title: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
  });

  // Added to track original values for reverting on Cancel
  const [originalValues, setOriginalValues] = useState({});

  const [editing, setEditing] = useState({
    title: false,
    first_name: false,
    middle_name: false,
    last_name: false,
    date_of_birth: false,
    gender: false,
  });

  /* LOAD (EDIT MODE ONLY)*/
  useEffect(() => {
    if (isCreate) return;

    loadSubject().then((res) => {
      if (!res) return;
      const data = {
        title: res.title || "",
        first_name: res.first_name || "",
        middle_name: res.middle_name || "",
        last_name: res.last_name || "",
        date_of_birth: res.date_of_birth || "",
        gender: res.gender || "",
      };
      setValues(data);
    });
  }, [isCreate, subjectId]);

  /* CREATE MODE → FORM MODEL */
  useEffect(() => {
    if (!isCreate || !form?.setValue) return;

    form.setValue({
      key: "subjects",
      value: {
        ...(form.model?.subjects || {}),
        title: values.title || null,
        first_name: values.first_name || null,
        middle_name: values.middle_name || null,
        last_name: values.last_name || null,
        date_of_birth: values.date_of_birth || null,
        gender: values.gender || null,
      },
    });
  }, [
    values.title,
    values.first_name,
    values.middle_name,
    values.last_name,
    values.date_of_birth,
    values.gender,
  ]);

  const handleChange = (field, val) => {
    setValues((prev) => ({ ...prev, [field]: val }));
  };

  const toggleEdit = (field) => {
    if (isCreate) return;
    
    setOriginalValues((prev) => ({ ...prev, [field]: values[field] }));
    setEditing((prev) => ({ ...prev, [field]: true }));
  };

  
  const cancelEdit = (field) => {
    setValues((prev) => ({ ...prev, [field]: originalValues[field] }));
    setEditing((prev) => ({ ...prev, [field]: false }));
  };

  const saveField = async (field) => {
    if (isCreate) return;

    setEditing((prev) => ({ ...prev, [field]: false }));

    const payload = {
      subjects: {
        id: subjectId,
        [field]: values[field],
      },
    };

    const res = await saveSubjectDirect(payload);
    if (!res) {
        alert("Save failed");
        // Revert UI if save fails
        cancelEdit(field);
    }
  };

  const renderField = (label, field, type = "text", isSelect = false) => {
    const isEditing = editing[field];
    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
    };

    const inputStyle = {
      backgroundColor: isEditing ? "#fff" : pillBg,
      border: isEditing ? `2px solid ${purple}` : "0",
      boxShadow: isEditing ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      padding: isEditing
        ? "0.5rem 105px 0.5rem 0.5rem" 
        : "0.5rem 2rem 0.5rem 0.5rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: isCreate ? "pointer" : "text",
    };

    let displayValue = values[field];
    if (isSelect) {
      const options = field === "gender" ? genders : titles;
      const selectedOption = options.find((o) => o.id === values[field]);
      if (selectedOption)
        displayValue = selectedOption.label || selectedOption.id;
    }

    return (
      <div className="mb-3">
        <label className="form-label mb-1" style={{ fontSize: 16 }}>
          {label}
        </label>

        <div style={{ position: "relative" }}>
          {isCreate &&
            (isSelect ? (
              <select
                value={values[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                className="form-select rounded-pill"
                style={inputStyle}
                title={displayValue}
              >
                <option value="">Select...</option>
                {(field === "gender" ? genders : titles).map((o, i) => (
                  <option key={i} value={o.id}>
                    {o.label || o.id}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={type}
                value={values[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                className="form-control rounded-pill"
                style={inputStyle}
                title={values[field]}
              />
            ))}

          {!isCreate && !isEditing && (
            <>
              {isSelect ? (
                <div
                  className="form-control rounded-pill border-0"
                  style={viewStyle}
                  title={displayValue}
                >
                  {displayValue || "Select..."}
                </div>
              ) : (
                <input
                  disabled
                  type={type}
                  value={values[field]}
                  className="form-control rounded-pill border-0"
                  style={viewStyle}
                  title={values[field]}
                />
              )}

              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(field)}
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  fontSize: 14,
                  zIndex: 2,
                }}
              />
            </>
          )}

          {!isCreate && isEditing && (
            <>
              {isSelect ? (
                <select
                  value={values[field]}
                  onChange={(e) => handleChange(field, e.target.value)}
                  className="form-select rounded-pill"
                  style={inputStyle}
                >
                  <option value="">Select...</option>
                  {(field === "gender" ? genders : titles).map((o, i) => (
                    <option key={i} value={o.id}>
                      {o.label || o.id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={type}
                  value={values[field]}
                  onChange={(e) => handleChange(field, e.target.value)}
                  className="form-control rounded-pill"
                  style={inputStyle}
                />
              )}

              <div
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  display: "flex",
                  gap: "4px",
                }}
              >
                <button
                  onClick={() => saveField(field)}
                  className="btn btn-sm"
                  style={{
                    background: purple,
                    color: "#fff",
                    borderRadius: 8,
                    padding: "2px 8px",
                    fontSize: "11px",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => cancelEdit(field)}
                  className="btn btn-sm btn-light"
                  style={{
                    borderRadius: 8,
                    padding: "2px 8px",
                    fontSize: "11px",
                    cursor: "pointer",
                    border: "1px solid #ccc",
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  /* RENDER */
  return (
    <div className="my-3" style={{ maxWidth: 490 }}>
      <div className="p-3">
        <div
          className="d-flex justify-content-between align-items-center mb-3"
          style={{ userSelect: "none" }}
        >
          <div className="d-flex align-items-center">
            <span style={{ fontSize: 30, color: "#b2a9ff", marginRight: 6 }}>
              ⋮⋮
            </span>
            <h5
              className="fw-bold m-0"
              style={{ color: purple, cursor: "pointer" }}
              onClick={() => setOpen(!open)}
            >
              Personal Information
            </h5>
          </div>

          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              backgroundColor: purple,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
            onClick={() => setOpen(!open)}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: 14 }} />
          </div>
        </div>

        {open && (
          <>
            {renderField("Title", "title", "text", true)}
            {renderField("First Name", "first_name")}
            {renderField("Middle Name", "middle_name")}
            {renderField("Last Name", "last_name")}
            {renderField("Date of Birth", "date_of_birth", "date")}
            {renderField("Gender", "gender", "text", true)}
          </>
        )}
      </div>
    </div>
  );
};

export default PersonalInformation;